import { useContext, useMemo } from 'react';

import { OptimizationContainer } from '@cast/types';

import { isJvmContainer } from 'features/workload-optimization';

import { JvmMetricKey } from './_constants';
import { prepareJvmChartData, prepareJvmMetrics } from './_utils';
import { useDisabledMetricsBoxes } from '../../../_hooks/useDisabledMetricsBoxes';
import { useRightsizingTimeRangeSelection } from '../../../_hooks/useRightsizingTimeRangeSelection';
import { WorkloadDetailsContext } from '../../../workload-details/_context';

export const useJvmChartData = ({
  container,
  timeZone: timeZoneProp,
}: {
  container?: OptimizationContainer;
  timeZone?: string;
}) => {
  const { timeZone: rightsizingTimeZone } = useRightsizingTimeRangeSelection();
  const timeZone = timeZoneProp ?? rightsizingTimeZone;
  const { workload, metrics, isLoading, isFetching, error, refetch } =
    useContext(WorkloadDetailsContext);

  const { toggleMetric, isMetricDisabled } =
    useDisabledMetricsBoxes<JvmMetricKey>();

  const containerMetrics = useMemo(
    () => metrics?.containers.find(({ name }) => name === container?.name),
    [metrics?.containers, container?.name]
  );

  const chartData = useMemo(() => {
    const jvm =
      container && isJvmContainer(container)
        ? containerMetrics?.jvm
        : undefined;
    return {
      chartData: prepareJvmChartData(jvm),
      metrics: prepareJvmMetrics(jvm),
    };
  }, [container, containerMetrics]);

  return {
    workload,
    timeZone,
    isLoading,
    isFetching,
    error,
    refetch,
    chartData,
    toggleMetric,
    isMetricDisabled,
  };
};
